// src/pages/ConfirmDelete.js
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { clearAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';

const ConfirmDelete = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('confirming'); // 'confirming', 'success', 'error'
  const [message, setMessage] = useState('');
  const [scheduledDelete, setScheduledDelete] = useState(null);
  const hasConfirmedRef = useRef(false);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  useEffect(() => {
    if (hasConfirmedRef.current || status !== 'confirming') {
      return;
    }
    
    hasConfirmedRef.current = true;

    const confirmDeletion = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Deletion token is missing. Please check your email and use the complete confirmation link.');
        showError('Deletion token is missing');
        return;
      }

      try {
        const response = await api.post(`/api/profile/me/confirm-delete/${token}`);
        
        setStatus('success');
        setMessage(response.data.message || 'Account deletion confirmed successfully.');
        setScheduledDelete(response.data.scheduledHardDelete);
        showSuccess('Account deletion confirmed');
        
        // Clear auth and redirect after 5 seconds
        setTimeout(() => {
          clearAuth();
          navigate('/login');
        }, 5000);
      } catch (err) {
        setStatus('error');
        let errorMsg = 'Account deletion confirmation failed';
        if (err.response?.data?.error) {
          if (typeof err.response.data.error === 'string') {
            errorMsg = err.response.data.error;
          } else if (err.response.data.error.message) {
            errorMsg = err.response.data.error.message;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        setMessage(errorMsg);
        showError(errorMsg);
      }
    };

    confirmDeletion();
  }, [token, navigate, showSuccess, showError, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-8">
          Account Deletion
        </h2>

        {status === 'confirming' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700">
              Confirming account deletion...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2">
              Deletion Confirmed
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            {scheduledDelete && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-400 dark:border-yellow-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Permanent deletion scheduled:</strong> {new Date(scheduledDelete).toLocaleString()}
                </p>
              </div>
            )}
            <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
              Redirecting you to login...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2">
              Confirmation Failed
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/profile')}
                className="w-full bg-shayek-light-200 dark:bg-shayek-dark-200 text-shayek-light-900 dark:text-shayek-dark-900 py-2 px-4 rounded-lg hover:bg-shayek-light-300 dark:hover:bg-shayek-dark-300 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
              >
                Back to Profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmDelete;

